import React, { useContext, useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Check, Collection, X } from 'react-bootstrap-icons';
import { AppStateContext } from '../../contexts/AppStateContext';
import { useTheme } from '../../contexts/ThemeContext';
import analyticsService from '../../services/analyticsService';
import { headerLabeledIconButtonStyle } from '../../utils/headerIconButtonStyle';
import {
  formatLayoutSummary,
  nextLayoutName,
  savedLayoutMatchesWorkspace,
} from '../../utils/savedChartLayouts';

const iconButtonStyle = (colors, { disabled = false } = {}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  padding: 0,
  borderRadius: 6,
  border: `1px solid ${colors.border}`,
  background: 'transparent',
  color: disabled ? colors.textLight : colors.text,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.45 : 1,
  flexShrink: 0,
});

const SavedChartLayoutsMenu = ({ variant = 'default', showLabel = false }) => {
  const { colors, isLight } = useTheme();
  const {
    charts = [],
    chartLayout = 'single',
    savedChartLayouts = [],
    saveChartLayout,
    deleteChartLayout,
    applySavedChartLayout,
  } = useContext(AppStateContext) || {};
  const isHeaderIcon = variant === 'headerIcon';
  const [show, setShow] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftName, setDraftName] = useState('');
  const inputRef = useRef(null);
  const layouts = Array.isArray(savedChartLayouts) ? savedChartLayouts : [];

  useEffect(() => {
    if (drafting && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [drafting]);

  const closeMenu = () => {
    setShow(false);
    setDrafting(false);
    setDraftName('');
  };

  const startDraft = () => {
    if (drafting) {
      inputRef.current?.focus();
      inputRef.current?.select();
      return;
    }
    setDraftName(nextLayoutName(layouts));
    setDrafting(true);
    setShow(true);
  };

  const cancelDraft = () => {
    setDrafting(false);
    setDraftName('');
  };

  const commitDraft = async () => {
    const name = draftName.trim();
    if (!name || typeof saveChartLayout !== 'function') return;
    const saved = await saveChartLayout(name);
    if (!saved) return;
    analyticsService.trackTradingEvent('chart_layout_saved', {
      chart_count: saved.charts?.length || 0,
      chart_layout: saved.chartLayout,
    });
    setDrafting(false);
    setDraftName('');
  };

  const applyLayout = async (layout) => {
    if (typeof applySavedChartLayout !== 'function') return;
    await applySavedChartLayout(layout.id);
    analyticsService.trackTradingEvent('chart_layout_applied', {
      chart_count: layout.charts?.length || 0,
      chart_layout: layout.chartLayout,
    });
    closeMenu();
  };

  const removeLayout = async (event, layoutId) => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof deleteChartLayout !== 'function') return;
    await deleteChartLayout(layoutId);
  };

  const toggleStyle = isHeaderIcon
    ? headerLabeledIconButtonStyle(colors, { isLight, active: show })
    : {
        backgroundColor: colors.bgSecondary,
        border: `1px solid ${colors.border}`,
        color: colors.text,
        padding: '6px 12px',
        fontSize: 14,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        borderRadius: 6,
        width: showLabel ? '100%' : undefined,
      };

  const menuItemStyle = {
    background: 'transparent',
    border: 'none',
    color: colors.text,
    width: '100%',
    textAlign: 'left',
    padding: '8px 12px',
    borderRadius: 6,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
  };

  return (
    <Dropdown
      show={show}
      autoClose="outside"
      align={isHeaderIcon ? 'end' : undefined}
      onToggle={(next) => {
        setShow(next);
        if (!next) {
          setDrafting(false);
          setDraftName('');
        }
      }}
    >
      <Dropdown.Toggle
        variant="secondary"
        className="header-dropdown-no-caret"
        style={toggleStyle}
        title="Saved layouts"
      >
        <Collection size={16} />
        {(isHeaderIcon || showLabel) && <span>Layout</span>}
      </Dropdown.Toggle>

      <Dropdown.Menu
        style={{
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: 4,
          minWidth: 280,
          maxWidth: 360,
          maxHeight: 420,
          overflowY: 'auto',
        }}
      >
        <button type="button" onClick={startDraft} style={menuItemStyle}>
          Save new layout
        </button>

        <div role="separator" style={{ height: 1, background: colors.border, margin: '4px 8px' }} />

        {drafting && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 8px',
            }}
          >
            <input
              ref={inputRef}
              aria-label="Layout name"
              value={draftName}
              maxLength={40}
              onChange={(event) => setDraftName(event.target.value)}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitDraft();
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  cancelDraft();
                }
              }}
              onClick={(event) => event.stopPropagation()}
              style={{
                flex: 1,
                minWidth: 0,
                background: colors.bgPrimary || colors.bgSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                color: colors.text,
                padding: '6px 8px',
                fontSize: 13,
                outline: 'none',
              }}
            />
            <button
              type="button"
              aria-label="Save layout"
              title="Save layout"
              disabled={!draftName.trim()}
              onClick={commitDraft}
              style={iconButtonStyle(colors, { disabled: !draftName.trim() })}
            >
              <Check size={16} />
            </button>
            <button
              type="button"
              aria-label="Cancel layout"
              title="Cancel"
              onClick={cancelDraft}
              style={iconButtonStyle(colors)}
            >
              <X size={16} />
            </button>
          </div>
        )}

        {layouts.map((layout) => {
          const active = savedLayoutMatchesWorkspace(layout, charts, chartLayout);
          return (
            <div
              key={layout.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                borderRadius: 6,
                backgroundColor: active ? `${colors.primary}20` : 'transparent',
              }}
            >
              <button
                type="button"
                onClick={() => applyLayout(layout)}
                style={{
                  ...menuItemStyle,
                  flex: 1,
                  minWidth: 0,
                  color: active ? colors.primary : colors.text,
                  fontWeight: active ? 600 : 400,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 2,
                }}
              >
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
                >
                  {layout.name}
                </span>
                <span style={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: colors.textLight,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
                >
                  {formatLayoutSummary(layout)}
                </span>
              </button>
              <button
                type="button"
                aria-label={`Delete ${layout.name}`}
                title="Delete layout"
                onClick={(event) => removeLayout(event, layout.id)}
                style={{ ...iconButtonStyle(colors), marginRight: 6 }}
              >
                <X size={14} />
              </button>
            </div>
          );
        })}

        {!drafting && layouts.length === 0 && (
          <div style={{ padding: '8px 12px', fontSize: 12, color: colors.textLight }}>
            No saved layouts
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default SavedChartLayoutsMenu;
